import { useMemo, useState } from "react";
import useAdminQuery from "../../lib/queries/useAdminQuery";
import { useFxRates } from "../../lib/queries/useFxRates";
import { Topbar } from "../../components/AppShell";
import { LoadingPanel } from "../../components/LoadingPanel";
import {
  STRIPE_MRR_HISTORICAL_SQL,
  STRIPE_MRR_CURRENT_SQL,
  PRODUCT_SPLIT_SQL,
  STRIPE_PER_SUB_DELTA_SQL,
  ACTIVE_USERS_SQL,
  ACTIVE_USERS_BY_ROLE_SQL,
  mapStripeMrrRows,
  mapCurrentMrrRows,
  mapProductSplitRows,
  mapPerSubRows,
  mapActiveUserRows,
  mapActiveUserByRoleRows,
} from "./sql";
import { Detail } from "./Detail";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import type { RegionKey, DashboardData } from "./sql";
import {
  REGIONS,
  RNAMES,
  COLORS,
  CCY,
  MONTHS,
  MONTH_YMS,
  CURRENT_MONTH_IDX,
  idxToYearMonth,
  yearMonthToIdx,
  fmt,
  buildData,
} from "./helpers";
import { buildFxMaps, buildFxMonthCloseMaps, computeFxShifts, fxFromDate, FX_BASE, FX_QUOTES } from "./fx";
import type { FxShift, FxArrMap } from "./fx";

const DB = { database: "stripe" };

function formatError(err: unknown): string {
  if (err == null) return "unknown error";
  if (err instanceof Error) return err.message || err.toString();
  if (typeof err === "string") return err;
  try { return JSON.stringify(err); } catch { return String(err); }
}

function FxPairsBadge({ shifts }: { shifts: Record<string, FxShift> }) {
  const entries = FX_QUOTES
    .flatMap((q) => {
      const shift = shifts[q];
      return shift ? [{ quote: q as string, shift }] : [];
    })
    .sort((a, b) => a.quote.localeCompare(b.quote));
  const first = entries[0];
  if (!first) return null;
  const latestDate = first.shift.latestDate;
  const baselineDate = first.shift.baselineDate;
  return (
    <div
      title={`Frankfurter daily reference rates. Conversion uses ${latestDate} (latest). Shift = local currency vs USD since ${baselineDate}.`}
      style={{ fontSize: 11, color: "var(--fg-3)", lineHeight: 1.4, textAlign: "right" }}
    >
      <table style={{ marginLeft: "auto", borderCollapse: "collapse", fontFamily: "var(--font-mono)" }}>
        <tbody>
          {entries.map(({ quote, shift }) => {
            const pct = shift.pctLocalVsUsd * 100;
            const positive = pct >= 0;
            const color = positive ? "var(--color-jade)" : "var(--fg-danger)";
            const arrow = positive ? "↑" : "↓";
            return (
              <tr key={quote}>
                <td style={{ padding: "0 10px 0 0", fontSize: 13, fontWeight: 600, color: "var(--fg-1)" }}>
                  {FX_BASE}/{quote} {shift.latest.toFixed(4)}
                </td>
                <td style={{ fontSize: 12, fontWeight: 700, color, textAlign: "right" }}>
                  {quote} {arrow}{Math.abs(pct).toFixed(2)}%
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ marginTop: 2 }}>
        as of {latestDate} · shift since {baselineDate}
      </div>
    </div>
  );
}

function Spark({
  values,
  color,
  months,
  sym,
}: {
  values: number[];
  color: string;
  months: string[];
  sym: string;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const c = values.map((v) => v ?? 0);
  const mx = Math.max(...c) * 1.2 || 1;
  const w = 100 / c.length;
  return (
    <div style={{ position: "relative", width: "100%", height: 28 }}>
      <svg viewBox="0 0 100 28" style={{ width: "100%", height: 28, display: "block" }} preserveAspectRatio="none">
        {c.map((v, i) => (
          <rect
            key={i}
            x={i * w} y={0}
            width={w} height={28}
            fill="transparent"
            style={{ cursor: v === 0 ? "default" : "pointer" }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          />
        ))}
        {c.map((v, i) => (
          <rect
            key={`b-${i}`}
            x={i * w + 1} y={v === 0 ? 0 : 28 - (v / mx) * 28}
            width={w - 2} height={v === 0 ? 28 : (v / mx) * 28}
            fill={v === 0 ? "var(--border-default)" : color}
            rx="1"
            opacity={v === 0 ? 0.25 : hovered != null && hovered !== i ? 0.4 : 1}
            style={{ pointerEvents: "none", transition: "opacity 0.15s" }}
          />
        ))}
      </svg>
      {hovered != null && (
        <div
          style={{
            position: "absolute",
            left: `${hovered * w + w / 2}%`,
            bottom: 34,
            transform: "translateX(-50%)",
            background: "var(--bg-1)",
            border: "1px solid var(--border-default)",
            borderRadius: 6,
            padding: "6px 8px",
            fontSize: 11,
            lineHeight: 1.3,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            zIndex: 10,
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          }}
        >
          <div style={{ color: "var(--fg-3)", fontSize: 10 }}>{months[hovered]}</div>
          <div style={{ fontFamily: "var(--font-mono)", fontWeight: 700 }}>
            {c[hovered] === 0 ? "—" : `${sym}${fmt(c[hovered])}`}
          </div>
        </div>
      )}
    </div>
  );
}

// Map app-DB country codes to dashboard regions.
const COUNTRY_TO_REGION: Record<string, RegionKey> = {
  NZ: "NZ",
  AU: "AU",
  GB: "UK",
  UK: "UK",
};

type AppDbProductSplit = {
  Pro: { firms: Record<RegionKey, number>; seats: Record<RegionKey, number> };
  Plus: { firms: Record<RegionKey, number>; seats: Record<RegionKey, number> };
};

function buildAppDbProductSplit(
  activeUsers: ReturnType<typeof mapActiveUserRows>,
): AppDbProductSplit {
  const empty = () => ({ NZ: 0, AU: 0, UK: 0, Other: 0 } as Record<RegionKey, number>);
  const out: AppDbProductSplit = {
    Pro: { firms: empty(), seats: empty() },
    Plus: { firms: empty(), seats: empty() },
  };
  for (const row of activeUsers) {
    const plan = (row.billing_plan ?? "").toLowerCase();
    if (plan === "free") continue;
    const region = COUNTRY_TO_REGION[row.country_code] ?? "Other";
    const bucket: keyof AppDbProductSplit | null =
      plan.includes("pro") ? "Pro" : plan.includes("plus") ? "Plus" : null;
    if (!bucket) continue;
    out[bucket].firms[region] += row.firm_count;
    out[bucket].seats[region] += row.seat_count;
  }
  return out;
}


type Tab = "overview" | "detail";

export function MrrDashboard() {
  const [showUSD, setShowUSD] = useState(true);
  const [mi, setMi] = useState(CURRENT_MONTH_IDX);
  const [tab, setTab] = useState<Tab>("overview");

  const histResult = useAdminQuery(STRIPE_MRR_HISTORICAL_SQL, [], DB);
  const curResult = useAdminQuery(STRIPE_MRR_CURRENT_SQL, [], DB);
  const splitResult = useAdminQuery(PRODUCT_SPLIT_SQL, [], DB);
  const perSubResult = useAdminQuery(STRIPE_PER_SUB_DELTA_SQL, [], DB);
  const activeUsersResult = useAdminQuery(ACTIVE_USERS_SQL); // app DB, not stripe
  const activeUsersByRoleResult = useAdminQuery(ACTIVE_USERS_BY_ROLE_SQL); // app DB, not stripe
  const fxResult = useFxRates(fxFromDate(MONTH_YMS), FX_BASE, [...FX_QUOTES]);

  useMemo(() => {
    if (!perSubResult.data) return;
    const rows = mapPerSubRows(perSubResult.data.columns, perSubResult.data.rows as unknown[][]);
    const movers = rows
      .map((r) => ({ ...r, delta: r.current_mrr - r.prior_mrr }))
      .filter((r) => Math.abs(r.delta) >= 1)
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
    console.log(`[MRR] Per-sub MoM movers (${movers.length}/${rows.length} subs):`);
    console.table(movers.map((r) => ({
      ccy: r.ccy,
      customer: r.customer,
      bucket: r.bucket,
      prior: r.prior_mrr,
      current: r.current_mrr,
      delta: r.delta,
    })));
    // Console helpers for ad-hoc investigation. Type these in the browser console:
    //   mrrFindSub("Regis")        — search all subs (movers + stable) by name or sub_id
    //   mrrAllSubs()               — print every sub, sorted by current MRR desc
    //   mrrAllSubs("GBP")          — same, filtered by currency
    type WindowWithHelpers = {
      mrrFindSub?: (query: string) => void;
      mrrAllSubs?: (ccyFilter?: string) => void;
    };
    const w = window as unknown as WindowWithHelpers;
    const project = (r: typeof rows[number]) => ({
      ccy: r.ccy,
      customer: r.customer,
      bucket: r.bucket,
      prior: r.prior_mrr,
      current: r.current_mrr,
      gross: r.gross_mrr,
      delta: r.current_mrr - r.prior_mrr,
      sub_id: r.sub_id,
    });
    w.mrrFindSub = (query: string) => {
      const ql = (query ?? "").toLowerCase().trim();
      if (!ql) {
        console.log("[MRR] usage: mrrFindSub(\"Regis\")");
        return;
      }
      const matches = rows
        .filter((r) => r.customer.toLowerCase().includes(ql) || r.sub_id.toLowerCase().includes(ql))
        .map(project);
      if (matches.length === 0) {
        console.log(`[MRR] No subs matching "${query}"`);
      } else {
        console.log(`[MRR] ${matches.length} sub(s) matching "${query}":`);
        console.table(matches);
      }
    };
    w.mrrAllSubs = (ccyFilter?: string) => {
      const ccyU = (ccyFilter ?? "").toUpperCase();
      const filtered = ccyU ? rows.filter((r) => r.ccy === ccyU) : rows;
      const sorted = [...filtered]
        .sort((a, b) => b.current_mrr - a.current_mrr)
        .map(project);
      console.log(`[MRR] All subs (${sorted.length}${ccyU ? `, ${ccyU}` : ""}):`);
      console.table(sorted);
    };
  }, [perSubResult.data]);

  const isLoading =
    histResult.isLoading ||
    curResult.isLoading ||
    splitResult.isLoading ||
    perSubResult.isLoading ||
    fxResult.isLoading;
  const isError =
    histResult.isError ||
    curResult.isError ||
    splitResult.isError ||
    perSubResult.isError ||
    fxResult.isError;

  const D: DashboardData | null = useMemo(() => {
    if (
      !histResult.data ||
      !curResult.data ||
      !splitResult.data ||
      !perSubResult.data ||
      !fxResult.data
    ) {
      return null;
    }
    const historical = mapStripeMrrRows(histResult.data.columns, histResult.data.rows as unknown[][]);
    const current = mapCurrentMrrRows(curResult.data.columns, curResult.data.rows as unknown[][]);
    const productSplit = mapProductSplitRows(splitResult.data.columns, splitResult.data.rows as unknown[][]);
    const perSub = mapPerSubRows(perSubResult.data.columns, perSubResult.data.rows as unknown[][]);
    // Option A: prior month MRR = current month MRR (authoritative current SQL total)
    // minus NEW subs (subs in per-sub with prior_mrr = 0; they had no paid invoice
    // covering prior month-end). Anchoring to current SQL avoids any methodology drift
    // between current SQL and the per-sub diagnostic. Captures expansions in the month
    // they happened by leaving them in the prior-month aggregate.
    // Key by (ccy, region) so IE GBP new subs land against the GBP/Other row,
    // not GBP/UK. Rows from STRIPE_PER_SUB_DELTA_SQL carry region directly.
    const newSubsByKey: Record<string, number> = {};
    const k = (ccy: string, region: string): string => `${ccy}|${region}`;
    for (const row of perSub) {
      if (row.prior_mrr === 0 && row.current_mrr > 0) {
        const key = k(row.ccy, row.region);
        newSubsByKey[key] = (newSubsByKey[key] ?? 0) + row.current_mrr;
      }
    }
    const priorCommitment = current.map((r) => ({
      ccy: r.ccy,
      region: r.region,
      mrr_local: r.mrr_local - (newSubsByKey[k(r.ccy, r.region)] ?? 0),
    }));
    const { fxUsd, fxNzd } = buildFxMaps(fxResult.data.daily, MONTH_YMS);
    return buildData(historical, current, productSplit, fxUsd, fxNzd, priorCommitment);
  }, [histResult.data, curResult.data, splitResult.data, perSubResult.data, fxResult.data]);

  if (!D) {
    return (
      <>
        <Topbar crumbs={["Dashboards", "Growth Dashboard"]} />
        <div className="page">
          <div className="page-header">
            <h1>Growth Dashboard</h1>
          </div>
          <div className="btn-group" style={{ marginBottom: 16 }}>
            <button
              type="button"
              className={`btn ${tab === "overview" ? "is-active" : ""}`}
              onClick={() => setTab("overview")}
            >
              Overview
            </button>
            <button
              type="button"
              className={`btn ${tab === "detail" ? "is-active" : ""}`}
              onClick={() => setTab("detail")}
            >
              Detail
            </button>
          </div>
          {tab === "detail" ? (
            <Detail
              rows={
                activeUsersByRoleResult.data
                  ? mapActiveUserByRoleRows(
                      activeUsersByRoleResult.data.columns,
                      activeUsersByRoleResult.data.rows as unknown[][],
                    )
                  : []
              }
            />
          ) : (
            <>
              {isLoading && <LoadingPanel />}
              {isError && (
                <pre style={{ color: "var(--fg-danger)", whiteSpace: "pre-wrap", fontSize: 12 }}>
                  Failed to load data.{"\n"}
                  {histResult.isError && `historical: ${formatError(histResult.error)}\n`}
                  {curResult.isError && `current: ${formatError(curResult.error)}\n`}
                  {splitResult.isError && `split: ${formatError(splitResult.error)}\n`}
                  {perSubResult.isError && `per-sub: ${formatError(perSubResult.error)}\n`}
                  {fxResult.isError && `fx: ${formatError(fxResult.error)}\n`}
                </pre>
              )}
            </>
          )}
        </div>
      </>
    );
  }

  // Per-month-close FX (each month at its own end-of-month rate; current month
  // uses today's rate). fxTgt uses the prior month's close rate so targets are
  // locked the moment the prior month ends, and intra-month FX drift becomes a
  // headwind/tailwind against the locked target. Mirrors MRR Tracking.
  // fxResult.data is guaranteed non-null below because D's useMemo depends on it.
  const { fxUsdClose, fxUsdPriorClose } = buildFxMonthCloseMaps(
    fxResult.data!.daily,
    MONTH_YMS,
  );
  const fxU = (r: RegionKey, i: number): number =>
    (fxUsdClose as FxArrMap)[r]?.[i] ?? D.fxUsd[r][CURRENT_MONTH_IDX] ?? 1;
  const fxTgt = (r: RegionKey, i: number): number =>
    (fxUsdPriorClose as FxArrMap)[r]?.[i] ?? fxU(r, i);
  const fxN = (r: RegionKey, i: number) => D.fxNzd[r][i] ?? D.fxNzd[r][CURRENT_MONTH_IDX] ?? 1;
  const g = (r: RegionKey, i: number) => {
    const v = D.mrr[r]?.[i] ?? 0;
    return showUSD ? v * fxU(r, i) : v;
  };

  const today = new Date();
  const dayOfMonth = today.getDate();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const monthFraction = dayOfMonth / daysInMonth;

  const gtEOM = (r: RegionKey, i: number) => {
    const v = D.tgt[r]?.[i] ?? 0;
    return showUSD ? v * fxTgt(r, i) : v;
  };
  const sym = showUSD ? "$" : "NZ$";

  // Product split: convert per-region local currency to NZD, then optionally to USD (current-month FX)
  const proByRegion: Record<string, number> = {};
  const plusByRegion: Record<string, number> = {};
  for (const r of REGIONS) {
    const proNZD = (D.proMrr[r] || 0) * fxN(r, CURRENT_MONTH_IDX);
    const plusNZD = (D.retool[r] || 0) * fxN(r, CURRENT_MONTH_IDX);
    proByRegion[r] = showUSD ? proNZD * fxU("NZ", CURRENT_MONTH_IDX) : proNZD;
    plusByRegion[r] = showUSD ? plusNZD * fxU("NZ", CURRENT_MONTH_IDX) : plusNZD;
  }
  const plusT = REGIONS.reduce((s, r) => s + (plusByRegion[r] || 0), 0);
  const proT = REGIONS.reduce((s, r) => s + (proByRegion[r] || 0), 0);
  const grand = proT + plusT;

  // KPI data — same methodology as MRR Tracking: actuals at per-month close FX,
  // targets at locked prior-month close FX, MoM and % of Target both anchored on
  // the locked prior close so intra-month FX shows as a headwind/tailwind.
  const isCurrentMonth = mi === CURRENT_MONTH_IDX;
  const sumUsdAt = (i: number) =>
    REGIONS.reduce((s, r) => s + (D.mrr[r]?.[i] ?? 0) * fxU(r, i), 0);
  const sumTgtUsdAt = (i: number) =>
    REGIONS.reduce((s, r) => s + (D.tgt[r]?.[i] ?? 0) * fxTgt(r, i), 0);

  const currentUSD = Math.round(sumUsdAt(mi));
  const goalUSD = Math.round(sumTgtUsdAt(mi));
  const requiredUSD = goalUSD - currentUSD;

  const priorUSDAtPriorClose = mi > 0
    ? REGIONS.reduce(
        (s, r) => s + (D.mrr[r]?.[mi - 1] ?? 0) * fxTgt(r, mi),
        0,
      )
    : 0;
  const monthlyGrowthPct = priorUSDAtPriorClose > 0
    ? ((currentUSD - priorUSDAtPriorClose) / priorUSDAtPriorClose) * 100
    : 0;
  const pctOfTargetPct = goalUSD - priorUSDAtPriorClose > 0
    ? ((currentUSD - priorUSDAtPriorClose) / (goalUSD - priorUSDAtPriorClose)) * 100
    : 0;
  const pctThroughMonthPct = (D.pctThroughMonth ?? monthFraction) * 100;
  const onTrackOverall = isCurrentMonth
    ? pctOfTargetPct >= pctThroughMonthPct
    : pctOfTargetPct >= 100;
  const hasPriorMonth = mi > 0;


  return (
    <>
      <Topbar
        crumbs={["Dashboards", "Growth Dashboard"]}
        right={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="month"
              className="btn btn-sm"
              value={idxToYearMonth(mi)}
              min="2025-09"
              onChange={(e) => {
                const idx = yearMonthToIdx(e.target.value);
                if (idx >= 0 && idx <= CURRENT_MONTH_IDX) setMi(idx);
              }}
            />
            <div className="btn-group">
              <button
                type="button"
                className={`btn btn-sm ${!showUSD ? "is-active" : ""}`}
                onClick={() => setShowUSD(false)}
              >
                Local CCY
              </button>
              <button
                type="button"
                className={`btn btn-sm ${showUSD ? "is-active" : ""}`}
                onClick={() => setShowUSD(true)}
              >
                USD
              </button>
            </div>
          </div>
        }
      />
      <div className="page">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1>Growth Dashboard</h1>
            <p className="lede">
              Monthly Recurring Revenue by geography, product, and plan.
              <span className="badge b-green" style={{ marginLeft: 8, verticalAlign: "middle" }}>
                LIVE
              </span>
            </p>
          </div>
          {fxResult.data && tab === "overview" && (
            <FxPairsBadge
              shifts={computeFxShifts(
                fxResult.data.daily,
                MONTH_YMS[Math.max(0, CURRENT_MONTH_IDX - 1)] as string,
              )}
            />
          )}
        </div>

        {/* Tabs */}
        <div className="btn-group" style={{ marginBottom: 16 }}>
          <button
            type="button"
            className={`btn ${tab === "overview" ? "is-active" : ""}`}
            onClick={() => setTab("overview")}
          >
            Overview
          </button>
          <button
            type="button"
            className={`btn ${tab === "detail" ? "is-active" : ""}`}
            onClick={() => setTab("detail")}
          >
            Detail
          </button>
        </div>

        {tab === "detail" ? (
          <Detail
            rows={
              activeUsersByRoleResult.data
                ? mapActiveUserByRoleRows(
                    activeUsersByRoleResult.data.columns,
                    activeUsersByRoleResult.data.rows as unknown[][],
                  )
                : []
            }
          />
        ) : (
          <>
        {/* KPI row: always USD; follows the month picker */}
        <div className="kpi-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
          <div className="kpi">
            <div className="kpi-label">{isCurrentMonth ? "Current MRR" : `${MONTHS[mi]} MRR`}</div>
            <div className="kpi-value" style={{ fontFamily: "var(--font-mono)" }}>
              US${fmt(currentUSD)}
            </div>
            <div className="kpi-sub">
              {hasPriorMonth ? (
                <span
                  className="badge"
                  style={{
                    background: monthlyGrowthPct >= 0 ? "var(--color-jade-soft)" : "var(--color-coral-soft)",
                    color: monthlyGrowthPct >= 0 ? "var(--color-jade)" : "var(--fg-danger)",
                  }}
                >
                  {monthlyGrowthPct >= 0 ? "+" : ""}{monthlyGrowthPct.toFixed(2)}% MoM
                </span>
              ) : (
                <span className="badge b-gray">First tracked month</span>
              )}
            </div>
          </div>
          <div className="kpi">
            <div className="kpi-label">
              {isCurrentMonth ? "EoM Goal (30% MoM)" : `${MONTHS[mi]} Target (30% MoM)`}
            </div>
            <div className="kpi-value" style={{ fontFamily: "var(--font-mono)" }}>
              US${fmt(goalUSD)}
            </div>
            <div className="kpi-sub">
              <span
                className="badge"
                style={{
                  background: requiredUSD <= 0 ? "var(--color-jade-soft)" : "var(--color-coral-soft)",
                  color: requiredUSD <= 0 ? "var(--color-jade)" : "var(--fg-danger)",
                }}
              >
                {isCurrentMonth
                  ? requiredUSD > 0 ? `US$${fmt(requiredUSD)} to go` : "Target hit"
                  : requiredUSD <= 0 ? `Hit by US$${fmt(Math.abs(requiredUSD))}` : `Missed by US$${fmt(requiredUSD)}`}
              </span>
            </div>
          </div>
          <div className="kpi">
            <div className="kpi-label">% of Target</div>
            <div className="kpi-value" style={{ fontFamily: "var(--font-mono)" }}>
              {pctOfTargetPct.toFixed(1)}%
            </div>
            <div className="kpi-sub">
              <span
                className="badge"
                style={{
                  background: onTrackOverall ? "var(--color-jade-soft)" : "var(--color-coral-soft)",
                  color: onTrackOverall ? "var(--color-jade)" : "var(--fg-danger)",
                }}
              >
                {isCurrentMonth
                  ? `${pctThroughMonthPct.toFixed(0)}% through month`
                  : onTrackOverall ? "Hit" : "Missed"}
              </span>
            </div>
          </div>
        </div>

        {/* Geographic performance vs plan */}
        <GeoPerformance
          D={D}
          mi={mi}
          showUSD={showUSD}
          monthFraction={monthFraction}
          g={g}
          gtEOM={gtEOM}
          fxTgt={fxTgt}
        />

        {/* Geographic contribution donut (USD mode only) */}
        {showUSD && <GeoDonut D={D} />}

        {/* MRR by Geography table */}
        <MrrTable mi={mi} showUSD={showUSD} g={g} />

        {/* Product split — seats & firms from AppDB; MRR from Stripe */}
        <ProductSplit
          appDbSplit={buildAppDbProductSplit(
            activeUsersResult.data
              ? mapActiveUserRows(activeUsersResult.data.columns, activeUsersResult.data.rows as unknown[][])
              : [],
          )}
          sym={sym}
          proByRegion={proByRegion}
          plusByRegion={plusByRegion}
          proT={proT}
          plusT={plusT}
          grand={grand}
        />

        {/* Sources footer */}
        <div className="callout" style={{ lineHeight: 1.7 }}>
          <i className="ti ti-info-circle" />
          <div>
            <strong>Sources.</strong> Stripe MRR from Supabase using a hybrid methodology:
            historical months use invoice total (net of discounts) / billing interval;
            current month differentiates stable (cycle invoice), new (create invoice),
            and expansion/contraction (current commitment x keep_rate from update invoices).
            Pro/Plus split uses the same hybrid logic at the subscription_item level.
            Invoiced MRR from static-invoices.csv (Active=Yes rows by Country).
            Consolidated MRR = Stripe + Invoiced, per currency.
            Targets = each currency's prior-month close x 1.30.
            Headline USD figures = per-currency totals x FX rates.
          </div>
        </div>
          </>
        )}

      </div>
    </>
  );
}

// ── Geographic donut (USD mode) ──────────────────────────────────────────────

function GeoDonut({ D }: { D: DashboardData }) {
  const [hovered, setHovered] = useState<RegionKey | null>(null);
  const [tip, setTip] = useState({ x: 0, y: 0 });

  const contrib = REGIONS.map((r) => ({
    region: r,
    usd: (D.mrr[r]?.[CURRENT_MONTH_IDX] || 0) * (D.fxUsd[r][CURRENT_MONTH_IDX] ?? 1),
    priorUsd: (D.mrr[r]?.[CURRENT_MONTH_IDX - 1] || 0) * (D.fxUsd[r][CURRENT_MONTH_IDX - 1] ?? D.fxUsd[r][CURRENT_MONTH_IDX] ?? 1),
  }));
  const totalUSD = contrib.reduce((s, c) => s + c.usd, 0);
  const priorTotalUSD = contrib.reduce((s, c) => s + c.priorUsd, 0);
  const totalGrowthUSD = totalUSD - priorTotalUSD;

  const R = 110;
  const SW = 50;
  const C = 2 * Math.PI * R;
  let cum = 0;

  const segments = contrib.map((c) => {
    const frac = totalUSD > 0 ? c.usd / totalUSD : 0;
    const segLen = frac * C;
    const offset = cum;
    cum += segLen;
    const momPct = c.priorUsd > 0 ? (c.usd - c.priorUsd) / c.priorUsd : null;
    const growthUSD = c.usd - c.priorUsd;
    const growthShare = totalGrowthUSD > 0 ? growthUSD / totalGrowthUSD : 0;
    return { ...c, frac, segLen, offset, momPct, growthUSD, growthShare };
  });
  const sortedLegend = [...segments].sort((a, b) => b.usd - a.usd);
  const topGrower = [...segments].sort((a, b) => b.growthUSD - a.growthUSD)[0];

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-h">
        <div className="title-wrap">
          <h2>Geographic contribution</h2>
          <span className="sub">
            Current MRR, normalised to USD. Each region converts via its direct USD pair.
          </span>
        </div>
      </div>
      <div className="card-body" style={{ display: "flex", alignItems: "center", gap: 28, flexWrap: "wrap" }}>
        {/* Donut */}
        <div
          style={{ flexShrink: 0, position: "relative", width: 280, height: 280 }}
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top });
          }}
        >
          <svg viewBox="0 0 280 280" style={{ width: 280, height: 280, transform: "rotate(-90deg)" }}>
            <circle cx="140" cy="140" r={R} fill="none" stroke="var(--border-default)" strokeWidth={SW} />
            {segments.map((s) => (
              <circle
                key={s.region} cx="140" cy="140" r={R} fill="none"
                stroke={COLORS[s.region]} strokeWidth={SW}
                strokeDasharray={`${s.segLen} ${C - s.segLen}`}
                strokeDashoffset={-s.offset}
                opacity={hovered && hovered !== s.region ? 0.35 : 1}
                style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                onMouseEnter={() => setHovered(s.region)}
                onMouseLeave={() => setHovered(null)}
              />
            ))}
          </svg>
          <div
            style={{
              position: "absolute", inset: 0,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <div className="eyebrow" style={{ marginBottom: 2 }}>Total</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em" }}>
              US${Math.round(totalUSD).toLocaleString()}
            </div>
          </div>
          {hovered && (() => {
            const s = segments.find((x) => x.region === hovered);
            if (!s) return null;
            const momPositive = s.momPct != null && s.momPct >= 0;
            const momText =
              s.momPct == null ? "—" : `${momPositive ? "+" : ""}${(s.momPct * 100).toFixed(1)}%`;
            const momColor =
              s.momPct == null ? "var(--fg-3)" : momPositive ? "var(--color-jade)" : "var(--fg-danger)";
            const left = Math.min(tip.x + 14, 280 - 180);
            const top = Math.min(tip.y + 14, 280 - 110);
            return (
              <div
                style={{
                  position: "absolute",
                  left, top,
                  pointerEvents: "none",
                  background: "var(--bg-1)",
                  border: "1px solid var(--border-default)",
                  borderRadius: 6,
                  padding: "8px 10px",
                  boxShadow: "var(--shadow-2, 0 4px 12px rgba(0,0,0,0.12))",
                  fontSize: 12,
                  minWidth: 160,
                  zIndex: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: COLORS[s.region] }} />
                  <span style={{ fontWeight: 700 }}>{RNAMES[s.region]}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <span style={{ color: "var(--fg-3)" }}>MRR</span>
                  <span style={{ fontFamily: "var(--font-mono)", fontWeight: 700 }}>
                    US${Math.round(s.usd).toLocaleString()}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <span style={{ color: "var(--fg-3)" }}>% of total</span>
                  <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600 }}>
                    {(s.frac * 100).toFixed(1)}%
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <span style={{ color: "var(--fg-3)" }}>MoM</span>
                  <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600, color: momColor }}>
                    {momText}
                  </span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Legend table */}
        <div style={{ flex: 1, minWidth: 260, alignSelf: "stretch", display: "flex", flexDirection: "column" }}>
          <table className="t" style={{ marginBottom: 0, flex: 1, height: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th />
                <th>Region</th>
                <th className="num">MRR</th>
                <th className="num">% of Total</th>
                <th className="num">MoM</th>
              </tr>
            </thead>
            <tbody>
              {sortedLegend.map((s) => {
                const momPositive = s.momPct != null && s.momPct >= 0;
                const momColor =
                  s.momPct == null ? "var(--fg-3)" : momPositive ? "var(--color-jade)" : "var(--fg-danger)";
                const momText =
                  s.momPct == null ? "\u2014" : `${momPositive ? "+" : ""}${(s.momPct * 100).toFixed(1)}%`;
                return (
                  <tr key={s.region} style={{ height: `${100 / sortedLegend.length}%` }}>
                    <td style={{ width: 12 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[s.region] }} />
                    </td>
                    <td style={{ fontWeight: 600 }}>{RNAMES[s.region]}</td>
                    <td className="num mono" style={{ fontWeight: 700 }}>
                      US${Math.round(s.usd).toLocaleString()}
                    </td>
                    <td className="num mono" style={{ color: COLORS[s.region], fontWeight: 600 }}>
                      {(s.frac * 100).toFixed(1)}%
                    </td>
                    <td className="num mono" style={{ color: momColor, fontWeight: 600 }}>
                      {momText}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {totalGrowthUSD > 0 && topGrower && (
            <div
              style={{
                marginTop: "auto", paddingTop: 10, borderTop: "1px solid var(--border-default)",
                fontSize: 11, color: "var(--fg-3)", lineHeight: 1.5,
              }}
            >
              This month added{" "}
              <span style={{ color: "var(--color-jade)", fontWeight: 700, fontFamily: "var(--font-mono)" }}>
                +US${Math.round(totalGrowthUSD).toLocaleString()}
              </span>
              .{" "}{RNAMES[topGrower.region]} led with{" "}
              <span
                style={{
                  color: COLORS[topGrower.region], fontWeight: 700, fontFamily: "var(--font-mono)",
                }}
              >
                +US${Math.round(topGrower.growthUSD).toLocaleString()}
              </span>{" "}
              ({Math.round(topGrower.growthShare * 100)}% of growth).
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Geographic performance vs plan ───────────────────────────────────────────

function GeoPerformance({
  D,
  mi,
  showUSD,
  monthFraction,
  g,
  gtEOM,
  fxTgt,
}: {
  D: DashboardData;
  mi: number;
  showUSD: boolean;
  monthFraction: number;
  g: (r: RegionKey, i: number) => number;
  gtEOM: (r: RegionKey, i: number) => number;
  fxTgt: (r: RegionKey, i: number) => number;
}) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>Geographic performance vs plan</h2>
      <p style={{ fontSize: 12, color: "var(--fg-3)", marginBottom: 16 }}>
        Per-region current MRR, end-of-month target, and % of required growth captured.
        Month is {Math.round(monthFraction * 100)}% elapsed.
      </p>
      <div className="grid-2">
        {REGIONS.map((r) => {
          const actual = g(r, mi) || 0;
          const eomTarget = gtEOM(r, mi) || 0;
          const isCurrentMonth = mi === CURRENT_MONTH_IDX;

          const startLocal =
            isCurrentMonth && D.monthStart[r] != null
              ? D.monthStart[r]
              : D.mrr[r]?.[mi - 1] || 0;
          // Locked at prior-month-close FX so it matches the target lock
          // (intra-month FX drift then surfaces as a headwind / tailwind).
          const start = showUSD ? startLocal * fxTgt(r, mi) : startLocal;

          const requiredGrowth = eomTarget - start;
          const capturedGrowth = actual - start;
          const captured =
            isCurrentMonth && D.regionCaptured[r] != null
              ? D.regionCaptured[r]
              : requiredGrowth > 0
                ? capturedGrowth / requiredGrowth
                : actual >= eomTarget
                  ? 1
                  : 0;
          const capturedPct = captured * 100;
          const onTrack = captured >= monthFraction;
          const s = showUSD ? "$" : CCY[r];

          return (
            <div key={r} className="card" style={{ marginBottom: 0 }}>
              <div className="card-h">
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 12, height: 12, borderRadius: "50%", background: COLORS[r] }} />
                  <span style={{ fontSize: 15, fontWeight: 700 }}>{RNAMES[r]}</span>
                </div>
                <span
                  className="badge"
                  style={{
                    background: onTrack ? "var(--color-jade-soft)" : "var(--color-coral-soft)",
                    color: onTrack ? "var(--color-jade)" : "var(--fg-danger)",
                  }}
                >
                  {captured >= 1 ? "Target hit" : onTrack ? "On track" : "Behind"}
                </span>
              </div>
              <div className="card-body">
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "var(--fg-2)" }}>Current MRR</span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 16, fontWeight: 700 }}>
                      {s}{fmt(actual)}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "var(--fg-2)" }}>End-of-month target</span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 16, fontWeight: 700 }}>
                      {s}{fmt(eomTarget)}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 12, color: "var(--fg-3)" }}>Month-start baseline</span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 600, color: "var(--fg-3)" }}>
                      {s}{fmt(start)}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "var(--fg-2)" }}>Growth captured</span>
                    <span
                      style={{
                        fontFamily: "var(--font-mono)", fontSize: 16, fontWeight: 700,
                        color: capturedGrowth >= 0 ? "var(--color-jade)" : "var(--fg-danger)",
                      }}
                    >
                      {capturedGrowth >= 0 ? "+" : ""}{s}{fmt(capturedGrowth)}{" "}
                      <span style={{ fontSize: 11, color: "var(--fg-3)", fontWeight: 500 }}>
                        of {s}{fmt(requiredGrowth)} needed
                      </span>
                    </span>
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{ height: 10, background: "var(--bg-3)", borderRadius: 5, position: "relative", marginBottom: 4 }}>
                  <div style={{ width: "100%", height: "100%", borderRadius: 5, overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${Math.max(0, Math.min(capturedPct, 100))}%`,
                        height: "100%", background: COLORS[r], borderRadius: 5,
                        transition: "width 0.5s",
                      }}
                    />
                  </div>
                  {isCurrentMonth && (
                    <div
                      style={{
                        position: "absolute", left: `${monthFraction * 100}%`, top: -5,
                        width: 3, height: 20, background: "var(--fg-1)", borderRadius: 2,
                        transform: "translateX(-1.5px)",
                      }}
                    />
                  )}
                </div>
                <div
                  style={{
                    display: "flex", justifyContent: "space-between",
                    fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-3)",
                  }}
                >
                  <span>0%</span>
                  <span style={{ color: onTrack ? "var(--color-jade)" : COLORS[r], fontWeight: 600 }}>
                    {capturedPct.toFixed(1)}% of target captured
                  </span>
                  <span>100%</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── MRR by Geography table ───────────────────────────────────────────────────

function MrrTable({
  mi,
  showUSD,
  g,
}: {
  mi: number;
  showUSD: boolean;
  g: (r: RegionKey, i: number) => number;
}) {
  return (
    <div className="card">
      <div className="card-h">
        <div className="title-wrap">
          <h2>MRR by Geography</h2>
          <span className="sub">
            {showUSD ? "Converted to USD" : "Each region in its local currency"}, trend and growth rates
          </span>
        </div>
        <span className="badge b-gray">{MONTHS[mi]}</span>
      </div>
      <div className="table-wrap no-border-top">
        <table className="t">
          <thead>
            <tr>
              <th style={{ width: 140 }}>Region</th>
              <th>Trend</th>
              <th className="num">MRR</th>
              <th className="num">MoM</th>
              <th className="num">3-mo avg</th>
            </tr>
          </thead>
          <tbody>
            {REGIONS.map((r) => {
              const actual = g(r, mi);
              const prev = mi > 0 ? g(r, mi - 1) : 0;
              const mom = prev > 0 ? ((actual - prev) / prev) * 100 : null;
              const growthRates: number[] = [];
              for (let i = Math.max(1, mi - 2); i <= mi; i++) {
                const cur = g(r, i);
                const prv = g(r, i - 1);
                if (prv > 0) growthRates.push(((cur - prv) / prv) * 100);
              }
              const avgGrowth =
                growthRates.length > 0
                  ? growthRates.reduce((a, b) => a + b, 0) / growthRates.length
                  : null;
              const s = showUSD ? "$" : CCY[r];
              const momColor =
                mom == null ? "var(--fg-3)" : mom >= 0 ? "var(--color-jade)" : "var(--fg-danger)";
              const avgColor =
                avgGrowth == null ? "var(--fg-3)" : avgGrowth >= 0 ? "var(--color-jade)" : "var(--fg-danger)";
              return (
                <tr key={r}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 3, background: COLORS[r] }} />
                      <div>
                        <div style={{ fontWeight: 600 }}>
                          {r === "Other" ? "International" : r}
                        </div>
                        <div style={{ fontSize: 10, color: "var(--fg-3)" }}>{RNAMES[r]}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "0 12px", overflow: "visible" }}>
                    <Spark
                      values={MONTHS.map((_, i) => g(r, i))}
                      color={COLORS[r]}
                      months={MONTHS}
                      sym={s}
                    />
                  </td>
                  <td className="num mono" style={{ fontWeight: 700 }}>
                    {s}{fmt(actual)}
                  </td>
                  <td className="num mono" style={{ fontWeight: 700, color: momColor }}>
                    {mom == null ? "-" : `${mom >= 0 ? "+" : ""}${mom.toFixed(0)}%`}
                  </td>
                  <td className="num mono" style={{ fontWeight: 600, color: avgColor }}>
                    {avgGrowth == null ? "-" : `${avgGrowth >= 0 ? "+" : ""}${avgGrowth.toFixed(0)}%`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Product split (Pro / Plus) ───────────────────────────────────────────────

function ProductSplit({
  appDbSplit,
  sym,
  proByRegion,
  plusByRegion,
  proT,
  plusT,
  grand,
}: {
  appDbSplit: AppDbProductSplit;
  sym: string;
  proByRegion: Record<string, number>;
  plusByRegion: Record<string, number>;
  proT: number;
  plusT: number;
  grand: number;
}) {
  const products = [
    {
      label: "Pro",
      color: "var(--primary-5)",
      badgeClass: "b-blue",
      byRegion: proByRegion,
      total: proT,
      co: appDbSplit.Pro.firms,
      st: appDbSplit.Pro.seats,
    },
    {
      label: "Plus",
      color: "var(--violet-5)",
      badgeClass: "b-violet",
      byRegion: plusByRegion,
      total: plusT,
      co: appDbSplit.Plus.firms,
      st: appDbSplit.Plus.seats,
    },
  ];

  const firmsGrand = REGIONS.reduce(
    (s, r) => s + (appDbSplit.Pro.firms[r] || 0) + (appDbSplit.Plus.firms[r] || 0),
    0,
  );

  return (
    <div className="grid-2" style={{ marginBottom: 24 }}>
      {products.map((p) => {
        const coT = REGIONS.reduce((s, r) => s + (p.co[r] || 0), 0);
        return (
          <div key={p.label} className="card" style={{ marginBottom: 0 }}>
            <div className="card-h">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{p.label}</span>
                <span className={`badge ${p.badgeClass}`}>
                  {grand > 0 ? Math.round((p.total / grand) * 100) : 0}%
                </span>
              </div>
            </div>
            <div className="card-body">
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 28, fontWeight: 700, letterSpacing: "-0.03em", marginBottom: 6 }}>
                {sym}{Math.round(p.total).toLocaleString()}
              </div>
              <div style={{ display: "flex", gap: 20, marginBottom: 16, paddingBottom: 14, borderBottom: "1px solid var(--border-default)" }}>
                <div>
                  <div style={{ fontSize: 10, color: "var(--fg-3)", marginBottom: 2 }}>Firms</div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 14, fontWeight: 700 }}>{coT}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "var(--fg-3)", marginBottom: 2 }}>Avg MRR/firm</div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 14, fontWeight: 700 }}>
                    {coT > 0 ? `${sym}${Math.round(p.total / coT).toLocaleString()}` : "-"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "var(--fg-3)", marginBottom: 2 }}>% of total MRR</div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 14, fontWeight: 700 }}>
                    {grand > 0 ? Math.round((p.total / grand) * 100) : 0}%
                  </div>
                </div>
              </div>

              {/* Per-region breakdown */}
              <table className="t" style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th />
                    <th>Region</th>
                    <th className="num">MRR</th>
                    <th className="num" title="Share of total MRR across Pro + Plus">% of total</th>
                    <th className="num">Firms</th>
                    <th className="num" title="Share of total firms across Pro + Plus">% of total</th>
                  </tr>
                </thead>
                <tbody>
                  {REGIONS.map((r) => {
                    const v = p.byRegion[r] || 0;
                    const mrrPct = grand > 0 ? (v / grand) * 100 : 0;
                    const regionFirms = p.co[r] || 0;
                    const firmPct = firmsGrand > 0 ? (regionFirms / firmsGrand) * 100 : 0;
                    return (
                      <tr key={r}>
                        <td style={{ width: 12 }}>
                          <div style={{ width: 6, height: 6, borderRadius: 1.5, background: COLORS[r] }} />
                        </td>
                        <td style={{ fontWeight: 600 }}>{r === "Other" ? "Intl" : r}</td>
                        <td className="num mono" style={{ fontWeight: 700 }}>
                          {sym}{Math.round(v).toLocaleString()}
                        </td>
                        <td className="num mono">{mrrPct.toFixed(0)}%</td>
                        <td className="num mono" style={{ fontWeight: 600 }}>{regionFirms}</td>
                        <td className="num mono">{firmPct.toFixed(0)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
